import { BaseMarkerStateManager } from '../base/baseMarkerStateManager'
import { PolygonMarker } from './polygonMarker'
import { SquareHandle } from '../handles/squareHandle'
import { PolygonMarkerVertex } from './PolygonMarkerVertex'
import { PolygonMarkerEdge } from './PolygonMarkerEdge'
import { doesSegmentIntersectSegments, arePointsWithinDistance } from '../../helperFunctions'
import { MarkerModes, type Point, type PolygonMarkerState } from '../../types'
import { MARKER_GUIDELINE_STROKE_COLOR } from '../../constants'

export class PolygonMarkerStateManager extends BaseMarkerStateManager<PolygonMarker, PolygonMarkerState, SquareHandle> {
  public vertices: Array<PolygonMarkerVertex> = []
  public edges: Array<PolygonMarkerEdge> = []
  public addVertexHandle: SquareHandle
  public isClosed = false
  public guidelineStrokeColor = MARKER_GUIDELINE_STROKE_COLOR
  public _closingClickRadius = 10

  constructor(marker: PolygonMarker) {
    super(marker)
    this.marker = marker
    this.typeName = PolygonMarker.typeName
  }

  moveVertex(vertex: PolygonMarkerVertex, deltaX: number, deltaY: number) {
    vertex.edges.forEach((edge: PolygonMarkerEdge) => {
      if (edge.start.x === vertex.x && edge.start.y === vertex.y) {
        edge.moveStart(deltaX, deltaY)
      }
      if (edge.end.x === vertex.x && edge.end.y === vertex.y) {
        edge.moveEnd(deltaX, deltaY)
      }
    })
    vertex.move(deltaX, deltaY)
    if (this.mode !== MarkerModes.Create) this.marker.markerEditCallback(this.marker)
  }

  moveMarker(deltaX: number, deltaY: number) {
    this.vertices.forEach((vertex: PolygonMarkerVertex) => {
      vertex.move(deltaX, deltaY)
    })
    this.edges.forEach((edge: PolygonMarkerEdge) => {
      edge.move(deltaX, deltaY)
    })

    if (this.mode !== MarkerModes.Create) this.marker.markerEditCallback(this.marker)
  }

  insertVertex(x: number, y: number, atIndex: number) {
    const newVertex = new PolygonMarkerVertex(x, y)
    this.vertices.splice(atIndex, 0, newVertex)
    this.populateEdges(this.points)
  }

  appendVertex(x: number, y: number) {
    const newVertex = new PolygonMarkerVertex(x, y)
    if (this.vertices.length === 0) {
      this.vertices.push(newVertex)
      return
    }
    const firstVertex = this.vertices[0]
    const lastVertex = this.vertices[this.vertices.length - 1]
    const precedingEdge = new PolygonMarkerEdge(lastVertex.position, newVertex.position)
    const edgesSegmentArray = this.edges.map((edge) => ({
      start: edge.start,
      end: edge.end,
    }))
    const precedingEdgeSegment = {
      start: precedingEdge.start,
      end: precedingEdge.end,
    }
    const isPrecedingEdgeIntersectingExisting = doesSegmentIntersectSegments(precedingEdgeSegment, edgesSegmentArray)
    const isNewPointCloseToFirst = arePointsWithinDistance(
      firstVertex.position,
      newVertex.position,
      this.closingClickRadius
    )

    if (isPrecedingEdgeIntersectingExisting) {
      return
    }

    if (isNewPointCloseToFirst) {
      precedingEdge.end = firstVertex.position
      firstVertex.edges.push(precedingEdge)
    } else {
      newVertex.edges.push(precedingEdge)
      this.vertices.push(newVertex)
    }
    lastVertex.edges.push(precedingEdge)
    this.edges.push(precedingEdge)
  }

  public populateVertices(points: Array<Point>) {
    if (!points) return
    points.forEach((point) => {
      this.vertices.push(new PolygonMarkerVertex(point.x, point.y))
    })
  }

  public populateEdges(points: Array<Point>) {
    if (!points) return
    this.edges = []
    points.forEach((point, i) => {
      const startVertex = this.vertices[i]
      const endVertex = this.vertices[(i + 1) % points.length]
      const edge = new PolygonMarkerEdge(startVertex.position, endVertex.position)
      startVertex.appendEdge(edge)
      endVertex.appendEdge(edge)
      this.edges.push(edge)
    })
  }

  public restore(state: PolygonMarkerState): void {
    super.restore(state)
    this.points = state.points
    this.guidelineStrokeColor = state?.guidelineStrokeColor || MARKER_GUIDELINE_STROKE_COLOR
    this.marker.handles.populate()
  }

  public export(): PolygonMarkerState {
    return Object.assign(
      {
        points: this.points,
        guidelineStrokeColor: this.guidelineStrokeColor,
      },
      super.export()
    )
  }

  get points(): Array<Point> {
    return this.vertices.map((vertex) => vertex.position)
  }

  set points(points: Array<Point>) {
    this.vertices = []
    this.edges = []
    this.populateVertices(points)
    this.populateEdges(points)
  }

  get closingClickRadius(): number {
    return this._closingClickRadius * this.scalingRatio
  }

  set closingClickRadius(newRadius: number) {
    this._closingClickRadius = newRadius
  }
}
